import { ReceiptLong as ReceiptIcon } from '@mui/icons-material';
import { Badge, IconButton, Tooltip } from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useModules } from '../../contexts/ModulesContext';
import {
    attentionCount,
    E_INVOICE_CHANGED_EVENT,
    type EInvoiceSummary,
} from '../../domain/eInvoice';
import { moduleUnavailableReason } from '../../domain/modules';
import { useDeferredQuery } from '../../hooks/useQuery';

const SUMMARY_POLL_MS = 5 * 60 * 1000;

/**
 * The administrator's header warning (plan §6.4): the numbering drift that stops sending, or rows
 * that need a person. Renders nothing while the module is unavailable, so the core header does
 * not depend on the module.
 */
export default function EInvoiceDriftBadge() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { modules, loaded } = useModules();
    const available = loaded && moduleUnavailableReason(modules, 'e-invoice') === null;
    const { data, reload } = useDeferredQuery<{ data: EInvoiceSummary }>({
        path: '/e-invoice/summary',
        shouldExecute: available,
        pollInterval: SUMMARY_POLL_MS,
    });
    const summary = available ? data?.data : undefined;

    useEffect(() => {
        if (!available) {
            return undefined;
        }

        const refresh = () => reload();
        globalThis.addEventListener(E_INVOICE_CHANGED_EVENT, refresh);
        return () => globalThis.removeEventListener(E_INVOICE_CHANGED_EVENT, refresh);
    }, [available, reload]);

    if (summary === undefined) {
        return null;
    }

    const attention = attentionCount(summary.counts);
    let title: string;
    let content: string | number;

    if (summary.drift.drifted) {
        title = t('eInvoice.badge.drift', {
            invoiceNumber: summary.drift.invoiceNumber,
            providerNumber: summary.drift.providerNumber,
        });
        content = '!';
    } else if (attention > 0) {
        title = t('eInvoice.badge.attention', { count: attention });
        content = attention;
    } else {
        return null;
    }

    return (
        <Tooltip title={title}>
            <IconButton
                color="warning"
                onClick={() => navigate('/e-invoice')}
                size="small"
                sx={{ ml: 1 }}
                data-testid="e-invoice-header-badge"
                aria-label={title}
            >
                <Badge badgeContent={content} color="error">
                    <ReceiptIcon fontSize="small" />
                </Badge>
            </IconButton>
        </Tooltip>
    );
}
